import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import athleteApi from '../lib/api'
import axios from 'axios'

export interface Document {
  id: string
  type: string
  name: string
  uploadedAt?: string
  status: 'verified' | 'approved' | 'pending' | 'not-uploaded' | 'rejected'
  rejectionReason?: string
  url?: string
}

export interface CollaborationDocument {
  id: string
  url: string
  name: string
  title: string
  documentType: string
  uploadedAt: string
}

interface DocumentsState {
  items: Document[]
  loading: boolean
  error: string | null
  uploading: { [key: string]: boolean }
  collaborationDocuments: Record<string, CollaborationDocument[]>
  collaborationDocumentsLoading: Record<string, boolean>
  collaborationContractUploading: Record<string, boolean>
  collaborationDocumentDeleting: Record<string, boolean>
}

const initialState: DocumentsState = {
  items: [],
  loading: false,
  error: null,
  uploading: {},
  collaborationDocuments: {},
  collaborationDocumentsLoading: {},
  collaborationContractUploading: {},
  collaborationDocumentDeleting: {},
}

// Upload a contract document for a specific collaboration
export const uploadCollaborationContract = createAsyncThunk(
  'documents/uploadCollaborationContract',
  async (
    { collaborationId, file }: { collaborationId: string; file: File },
    { rejectWithValue },
  ) => {
    try {
      const contentType = file.type || 'application/pdf'

      const response = await athleteApi.post(
        `/athlete/campaigns/${collaborationId}/documents/upload-url`,
        { fileName: file.name, contentType },
      )
      const { presignedUrl, publicUrl, documentId } = response.data

      await axios.put(presignedUrl, file, { headers: { 'Content-Type': contentType } })

      const newDoc: CollaborationDocument = {
        id: documentId,
        url: publicUrl,
        name: file.name,
        title: file.name,
        documentType: 'contract',
        uploadedAt: new Date().toISOString(),
      }

      return { collaborationId, document: newDoc }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to upload contract')
    }
  },
)

// Delete a collaboration document
export const deleteCollaborationDocument = createAsyncThunk(
  'documents/deleteCollaborationDocument',
  async (
    { collaborationId, documentId }: { collaborationId: string; documentId: string },
    { rejectWithValue },
  ) => {
    try {
      await athleteApi.delete(`/athlete/campaigns/${collaborationId}/documents/${documentId}`)
      return { collaborationId, documentId }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to delete document')
    }
  },
)

// Fetch collaboration documents (uploaded by brand for athlete to review)
export const fetchCollaborationDocuments = createAsyncThunk(
  'documents/fetchCollaborationDocuments',
  async (collaborationId: string, { rejectWithValue }) => {
    try {
      const response = await athleteApi.get(`/athlete/campaigns/${collaborationId}/documents`)
      return { collaborationId, documents: response.data.documents as CollaborationDocument[] }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch documents')
    }
  },
)

// Fetch documents
export const fetchDocuments = createAsyncThunk(
  'documents/fetchDocuments',
  async (_, { rejectWithValue }) => {
    try {
      const response = await athleteApi.get('/athlete/documents')
      return response.data.documents
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch documents')
    }
  },
)

// Upload document
export const uploadDocument = createAsyncThunk(
  'documents/uploadDocument',
  async (
    { file, documentType }: { file: File; documentType: string },
    { rejectWithValue, dispatch },
  ) => {
    try {
      // 1. Prepare content type
      const contentType = file.type || 'application/octet-stream'

      // 2. Get presigned URL
      const response = await athleteApi.post('/athlete/documents/upload-url', {
        fileName: file.name,
        documentType,
        contentType,
      })

      const { presignedUrl, publicUrl, documentId } = response.data

      // 3. Upload to S3
      await axios.put(presignedUrl, file, {
        headers: {
          'Content-Type': contentType,
        },
      })

      // 3. Return the new document object (optimistic or constructed from response)
      // The backend creates the record immediately, so we can construct the object
      const newDocument: Document = {
        id: documentId,
        type: documentType,
        name: file.name,
        uploadedAt: new Date().toISOString(),
        status: 'pending',
        url: publicUrl,
      }

      return newDocument
    } catch (error: any) {
      console.error('Upload error:', error)
      return rejectWithValue(error.response?.data?.error || 'Failed to upload document')
    }
  },
)

const documentsSlice = createSlice({
  name: 'documents',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch documents
      .addCase(fetchDocuments.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchDocuments.fulfilled, (state, action: PayloadAction<Document[]>) => {
        state.loading = false
        state.items = action.payload
      })
      .addCase(fetchDocuments.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
      // Upload document
      .addCase(uploadDocument.pending, (state, action) => {
        // We can track uploading state by document type if passed in meta
        const documentType = action.meta.arg.documentType
        state.uploading[documentType] = true
      })
      .addCase(uploadDocument.fulfilled, (state, action) => {
        const documentType = action.meta.arg.documentType
        state.uploading[documentType] = false

        // Remove existing document of same type if it exists (update it)
        // Or should we keep history? For now, let's just prepend/replace
        // If we want to show only latest per type:
        // state.items = state.items.filter(d => d.type !== action.payload.type)
        // state.items.push(action.payload)

        // Actually, let's just add it. The sorting/filtering can be done in UI or selector if needed.
        // But duplicates might be confusing.
        // Let's replace if same type exists to keep list clean?
        // Or maybe just add to top.
        state.items.unshift(action.payload)
      })
      .addCase(uploadDocument.rejected, (state, action) => {
        const documentType = action.meta.arg.documentType
        state.uploading[documentType] = false
        state.error = action.payload as string
      })
      // Upload collaboration contract
      .addCase(uploadCollaborationContract.pending, (state, action) => {
        state.collaborationContractUploading[action.meta.arg.collaborationId] = true
      })
      .addCase(uploadCollaborationContract.fulfilled, (state, action) => {
        const { collaborationId, document } = action.payload
        state.collaborationContractUploading[collaborationId] = false
        const existing = state.collaborationDocuments[collaborationId] ?? []
        // Prepend the new contract, keeping other docs
        state.collaborationDocuments[collaborationId] = [document, ...existing]
      })
      .addCase(uploadCollaborationContract.rejected, (state, action) => {
        state.collaborationContractUploading[action.meta.arg.collaborationId] = false
        state.error = action.payload as string
      })
      // Fetch collaboration documents
      .addCase(fetchCollaborationDocuments.pending, (state, action) => {
        state.collaborationDocumentsLoading[action.meta.arg] = true
      })
      .addCase(fetchCollaborationDocuments.fulfilled, (state, action) => {
        const { collaborationId, documents } = action.payload
        state.collaborationDocumentsLoading[collaborationId] = false
        state.collaborationDocuments[collaborationId] = documents
      })
      .addCase(fetchCollaborationDocuments.rejected, (state, action) => {
        state.collaborationDocumentsLoading[action.meta.arg] = false
      })
      // Delete collaboration document
      .addCase(deleteCollaborationDocument.pending, (state, action) => {
        state.collaborationDocumentDeleting[action.meta.arg.documentId] = true
      })
      .addCase(deleteCollaborationDocument.fulfilled, (state, action) => {
        const { collaborationId, documentId } = action.payload
        state.collaborationDocumentDeleting[documentId] = false
        const existing = state.collaborationDocuments[collaborationId] ?? []
        state.collaborationDocuments[collaborationId] = existing.filter((d) => d.id !== documentId)
      })
      .addCase(deleteCollaborationDocument.rejected, (state, action) => {
        state.collaborationDocumentDeleting[action.meta.arg.documentId] = false
      })
  },
})

export default documentsSlice.reducer
